import { Box, FormControl } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import cn from 'classnames';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { RegisterOptions, UseFormRegister } from 'react-hook-form';

import styles from './styles.module.scss';

interface InputProps {
  wrapperClassName?: string;
  options: { id?: string | number | undefined; value: string; label: string }[];
  inputClass?: string;
  register?: UseFormRegister<any>;
  registerOptions?: RegisterOptions;
  errors?: { [key: string]: any };
  wrapperStyle?: React.CSSProperties;
  name: string;
  label?: string;
  onChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autoComplete?: string;
  onFocus?: () => void;
  value?: string;
  required?: boolean;
  disablePortal?: boolean;
  guardian_relationship?: boolean;
}

const MUISelectInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      options,
      inputClass,
      wrapperStyle,
      name,
      register,
      registerOptions,
      errors,
      label,
      onChange,
      onBlur,
      disabled = false,
      autoComplete,
      onFocus,
      value,
      required,
      disablePortal,
      guardian_relationship,
      ...props
    },
    ref,
  ) => {
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
      if (errors && name) {
        const nameArray = name.split('.');
        const error = nameArray.length > 1 ? errors[nameArray[0]]?.[nameArray[1]]?.message : errors[name]?.message;
        setErrorMessage(error || '');
      } else {
        setErrorMessage('');
      }
    }, [errors, name]);

    const selectedOption = useMemo(() => {
      if (value === '' || value === null || value === undefined) return null;

      return (
        options.find((option) => option.value === value) ||
        options.find((option) => option.label === value) ||
        null
      );
    }, [options, value]);

    return (
      <Box
        className={cn(styles.selectInputWrapper, wrapperClassName, disabled && styles.disabledField)}
        style={wrapperStyle}
        data-testid={`select-wrapper-${name}`}
      >
        <FormControl sx={{ border: '0', minHeight: '56px', width: '100%' }} error={!!errorMessage}>
          <Autocomplete
            disablePortal={disablePortal ?? true}
            // When the popup is portaled to <body> (disablePortal=false), lift it above modal
            // overlays so the list is reachable; inline poppers keep the default stacking.
            slotProps={disablePortal === false ? { popper: { sx: { zIndex: 10000 } } } : undefined}
            options={options}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, selected) => option.value === selected.value}
            fullWidth
            value={selectedOption}
            onChange={(_, newValue) => {
              if (onChange) {
                onChange(newValue ? (!guardian_relationship ? newValue.value : newValue.label) : '');
              }
            }}
            disabled={disabled}
            ref={ref}
            renderOption={(optionProps, option) => (
              <li {...optionProps} className={cn(optionProps.className, 'fs-exclude')}>
                {option.label}
              </li>
            )}
            {...props}
            renderInput={(params) => (
              <TextField
                {...params}
                className={cn(styles.input, inputClass)}
                label={label}
                autoComplete={autoComplete}
                onBlur={onBlur}
                onFocus={onFocus}
                InputLabelProps={{
                  className: 'fs-unmask',
                  required: Boolean(required),
                  shrink: Boolean(selectedOption) || undefined,
                  sx: {
                    fontSize: '14px',
                    color: disabled ? 'rgba(23, 36, 51, 0.38)' : 'rgba(23, 36, 51, 0.72)',
                    backgroundColor: 'white',
                    px: 0.3,
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(6px, -10px) scale(0.75)',
                    },
                    '&.Mui-focused': {
                      color: 'var(--primary-color)',
                    },
                    '& .MuiFormLabel-asterisk': {
                      color: '#d32f2f',
                      opacity: 1,
                      fontWeight: 700,
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                    transition: 'max-width 0.2s ease-out',
                  },
                  '& .MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                  },
                  '&&&& .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                  },

                  '& .MuiOutlinedInput-root': {
                    transition: 'all 0.2s ease',
                    backgroundColor: disabled ? '#f8fafc' : '#ffffff',
                  },
                  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                    borderColor: disabled ? 'rgba(23, 36, 51, 0.16)' : 'rgba(23, 36, 51, 0.24)',
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: disabled ? 'rgba(23, 36, 51, 0.16)' : 'rgba(23, 36, 51, 0.72)',
                  },
                  '& .MuiOutlinedInput-root:hover': {
                    backgroundColor: disabled ? '#f8fafc' : '#fafcff',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused': {
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 0 3px rgba(46, 115, 189, 0.08)',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--primary-color)',
                    borderWidth: '2px',
                  },

                  '& .MuiInputBase-input': {
                    color: disabled ? 'rgba(23, 36, 51, 0.45)' : 'rgba(23, 36, 51, 0.88)',
                    fontWeight: disabled ? 400 : 500,
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(23, 36, 51, 0.45)',
                  },
                }}
                error={!!errorMessage}
                helperText={errorMessage}
                inputProps={{
                  ...params.inputProps,
                  'aria-required': required || undefined,
                  'data-testid': `select-input-text-${name}`,
                  className: cn(params.inputProps.className, 'fs-exclude'),
                }}
                {...(register ? register(name, registerOptions) : {})}
              />
            )}
          />
        </FormControl>
      </Box>
    );
  },
);

export default MUISelectInput;
